/**
 * **Save as new template** (`discovery/ui-reference/templates/INDEX.md` §2).
 *
 * monday's dialog is a name prefilled with `<board name> template`, a Public/Private radio,
 * and a 💎 upsell for *managed* templates — which are its paid tier and are not built here,
 * so the card is rendered as the disabled note it is rather than omitted.
 *
 * There is **no** per-part opt-in in monday's dialog. Ours adds exactly one checkbox that
 * monday has no equivalent for — "Include the items" — because a structure-only template is
 * the thing people actually ask for and leaving it out would mean copying 500 rows every time.
 * It is labelled as ours, not as monday's.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useOverlayDismiss, useToast } from '@elizmonday/ui';
import { defaultTemplateName, type BoardTemplateKind } from '@elizmonday/shared';
import { useTRPCClient } from '../../lib/trpc';
import './templates.css';

export function SaveAsTemplateDialog({
  boardId,
  boardName,
  onClose,
}: {
  boardId: string;
  boardName: string;
  onClose(): void;
}) {
  const client = useTRPCClient();
  const toast = useToast();
  const [name, setName] = useState(defaultTemplateName(boardName));
  const [kind, setKind] = useState<BoardTemplateKind>('public');
  const [includeItems, setIncludeItems] = useState(true);

  /* P-013: Escape closes the dialog and hands focus back to whatever opened it. */
  useOverlayDismiss({ onClose });

  const save = useMutation({
    mutationFn: () => client.templates.save.mutate({ boardId, name: name.trim(), kind, includeItems }),
    onSuccess: (template) => {
      toast(`${template.name} saved as a template`, { kind: 'positive' });
      onClose();
    },
    onError: (error: unknown) => {
      toast(error instanceof Error ? error.message : 'The template could not be saved', {
        kind: 'negative',
      });
    },
  });

  return (
    <div className="ez-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <form
        className="ez-template-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Save as new template"
        data-testid="save-template-dialog"
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim() !== '') save.mutate();
        }}
      >
        <div className="ez-template-dialog__form">
          <h2>Save as new template</h2>

          <label className="ez-template-dialog__field">
            Name
            <input
              data-testid="save-template-name"
              value={name}
              maxLength={255}
              autoFocus
              onChange={(event) => setName(event.target.value)}
            />
          </label>

          <fieldset className="ez-template-dialog__radios">
            <legend>Template privacy</legend>
            <label>
              <input
                type="radio"
                name="template-kind"
                value="public"
                checked={kind === 'public'}
                data-testid="save-template-public"
                onChange={() => setKind('public')}
              />
              <span>Public</span>
            </label>
            <label>
              <input
                type="radio"
                name="template-kind"
                value="private"
                checked={kind === 'private'}
                data-testid="save-template-private"
                onChange={() => setKind('private')}
              />
              <span>🔒 Private</span>
            </label>
          </fieldset>
          <p className="ez-template-dialog__hint">
            Anyone can use this template. only invited people and admins can edit it.
          </p>

          <label className="ez-template-dialog__check">
            <input
              type="checkbox"
              data-testid="save-template-items"
              checked={includeItems}
              onChange={(event) => setIncludeItems(event.target.checked)}
            />
            <span>Include the items (ours — monday always copies them)</span>
          </label>

          <div className="ez-template-dialog__upsell" data-testid="save-template-managed">
            <strong>Create managed template</strong>
            <span>
              Editing managed templates will update all copies created from it. ideal for syncing
              multiple projects at once.
            </span>
            <em>Not available on this deployment.</em>
          </div>
        </div>

        <div className="ez-template-dialog__preview" aria-hidden="true" />

        <footer className="ez-template-dialog__foot">
          <button type="button" className="ez-template-dialog__cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="ez-template-dialog__save"
            data-testid="save-template-submit"
            disabled={save.isPending || name.trim() === ''}
          >
            Save template
          </button>
        </footer>
      </form>
    </div>
  );
}
